import {ArtfactKind, EnvironmentKind} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import {from, Observable, of} from 'rxjs';
import {concatMap, map} from 'rxjs/operators';
import bcrypt from 'bcrypt';
import jwt from 'jsonwebtoken';
import {HttpHook} from '../http/http.hook';

@HookClass('auth')
export class AuthHook extends BaseHook {

    @Hook('0ecd88d0-c7ed-4143-8b65-bdfde168e696')
    public register(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthService')
            .pipe(concatMap((service: any) => {
                const user = {
                    username: inputArtifact.object.username,
                    name: inputArtifact.object.name,
                    email: inputArtifact.object.email,
                    password: inputArtifact.object.password,
                    phone: inputArtifact.object.phone
                };

                return service.register(user, inputArtifact.object.ruleGroup);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('f77d6f51-4512-4b2b-ba0d-9865b7136d95')
    public registerUserWithSystem(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                const user = {
                    username: inputArtifact.object.username,
                    name: inputArtifact.object.name,
                    email: inputArtifact.object.email,
                    password: inputArtifact.object.password,
                    phone: inputArtifact.object.phone
                };

                return service.registerUser(user, inputArtifact.object.ruleGroup, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('1cdf25af-af0e-48c4-b75c-1ba9f392bf11')
    public currentUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => this.context.user))
            .pipe(map((user: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = user;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('ee9d59fa-06ec-4c54-8618-460972c5de35')
    public isAuthenticate(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.context.user.pipe(map((user: any) => {
            const artifact = new NickProcessArtfact();
            artifact.object = user;
            artifact.kind = ArtfactKind.ANY;
            return artifact;
        }));
    }

    @Hook('3b67e154-148d-44fc-b54f-853898360724')
    public changePassword(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthService')
            .pipe(concatMap((service: any) => {
                return service.changePassword(inputArtifact.object.data);
            }))
            .pipe(map((user: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = user;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('42a98a04-4fb0-4664-8115-19fb345fe54d')
    public deleteUserSystemXref(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthService')
            .pipe(concatMap((service: any) => {
                return service.deleteUserSystemXref(inputArtifact.object.userId);
            }))
            .pipe(map((user: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = user;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('89fc7399-76e3-42c2-b328-d3b2247c0906')
    public activateUserSystemXref(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthService')
            .pipe(concatMap((service: any) => {
                return service.activateUserSystemXref(inputArtifact.object.userId);
            }))
            .pipe(map((user: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = user;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('448e67c1-c1b7-4cfb-af7c-226c4f9718ac')
    public changeRuleGroup(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('UserService')
            .pipe(concatMap((service: any) => {
                return service.changeRuleGroup(inputArtifact.object.userId, inputArtifact.object.ruleGroupId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('9845b850-ce51-41e3-9071-f9397ae65192')
    public authenticate(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthService')
            .pipe(concatMap((service: any) => {
                let authData = inputArtifact.object;

                if (inputArtifact.object.config)
                    authData = inputArtifact.object.data;

                return service.authenticate(authData, inputArtifact.object.config?.generateToken);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('0d964fe9-9a75-4b10-86d0-9e358c8badfa')
    public authenticateBySystemId(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                let authData = inputArtifact.object;

                if (inputArtifact.object.config)
                    authData = inputArtifact.object.data;

                return service.authenticateUser(authData, inputArtifact.object.config?.generateToken, authData.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('3b66ee2a-2a1f-4d16-89a2-645d5a0de4bf')
    public createJWT(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        if (!inputArtifact.object.options)
            inputArtifact.object.options = {expiresIn: inputArtifact.object.expireTime};
        return of(jwt.sign(inputArtifact.object.payload, inputArtifact.object.secret, inputArtifact.object.options))
            .pipe(map((token: string) => {
                const artifact = new NickProcessArtfact();
                artifact.object = token;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('05ff8278-66e8-4d99-a6c8-fbb5ae529b1f')
    public verifyJWT(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(jwt.verify(inputArtifact.object.token, inputArtifact.object.secret, inputArtifact.object.options))
            .pipe(map((jwtPayload: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = jwtPayload;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }


    @Hook('1fc763d2-2597-49a8-93bb-6f49b8e9a396')
    public decodeJWT(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(jwt.decode(inputArtifact.object.token, inputArtifact.object.options))
            .pipe(map((jwtPayload: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = jwtPayload;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('fd930116-954b-413c-8522-bbc3be3af7d7')
    public hashPassword(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return from(bcrypt.hash(inputArtifact.object.password, inputArtifact.object.config?.saltRounds))
            .pipe(map((hash: string) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.object = {password: hash};
                outputArtifact.kind = ArtfactKind.ANY;
                return outputArtifact;
            }));
    }

    @Hook('0394faad-d6a8-4146-883d-bed524d15afd')
    public matchPassword(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return from(bcrypt.compare(inputArtifact.object.password, inputArtifact.object.passwordHash))
            .pipe(map((match: boolean) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.object = {match};
                outputArtifact.kind = ArtfactKind.ANY;
                return outputArtifact;
            }));
    }

    @Hook('87f696d4-d6aa-4272-923d-eaf265b0d884')
    public requestOTP(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.requestOTP(inputArtifact.object.userId, inputArtifact.object.expire);
            }))
            .pipe(map((otp: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = otp;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('1e3af2dc-1c1e-4781-8292-8700647bed9a')
    public authenticateByOTP(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.authenticateByOTP(inputArtifact.object.userId, inputArtifact.object.otp);
            }))
            .pipe(map((otp: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = otp;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('f1226e9b-36ff-45bb-9ef0-8e4ab01e0015')
    public countUsers(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.countUsers(inputArtifact.object.systemId);
            }))
            .pipe(map((otp: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = otp;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('fe22127d-35ec-44f3-be34-348398d65a23')
    public getUserListBySystem(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getUserListBySystem(inputArtifact.object.systemId, inputArtifact.object.page, inputArtifact.object.pageSize, inputArtifact.object.smartSearch, inputArtifact.object.filters, inputArtifact.object.sortCreatedAsc);
            }))
            .pipe(map((data: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = data;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }


    @Hook('8e17d805-aeac-4f7c-95a0-912b1b02e8f1')
    public registerParentUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                const parentUser = {
                    userId: inputArtifact.object.userId,
                    parentUserId: inputArtifact.object.parentUserId
                };

                return service.registerParentUser(parentUser, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('2eff1fa2-3960-400b-89e8-0e535d144bf5')
    public registerGroupUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                const user = {
                    username: inputArtifact.object.username,
                    name: inputArtifact.object.name,
                    email: inputArtifact.object.email,
                    password: inputArtifact.object.password,
                    picture: inputArtifact.object.picture,
                    phone: inputArtifact.object.phone
                };

                return service.registerGroupUser(user, inputArtifact.object.ruleGroup, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('e5e6fb53-e159-4945-a907-6b253788752d')
    public getGroupUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getGroupUser(inputArtifact.object.userId, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('d8616750-910c-4a36-bc06-0a7cf384a10d')
    public validateRole(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const role = inputArtifact.object.role;
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.validateRole(inputArtifact.object.role);
            }));
    }

    @Hook('fc6b1077-9bed-48b2-9957-823bb466c2d8')
    public getUsersByRole(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getUsersByRole(inputArtifact.object.role, inputArtifact.object.systemId);
            }));
    }

    @Hook('30fed985-75ee-4f37-9e26-cf5c68099865')
    public removeRoleFromUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.removeRoleFromUser(inputArtifact.object.userId, inputArtifact.object.roleId, inputArtifact.object.systemId);
            }));
    }

    @Hook('05211b69-f26d-4f3c-8555-16ee103b20be')
    public addRoleToUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.addRoleToUser(inputArtifact.object.userId, inputArtifact.object.roleId, inputArtifact.object.systemId);
            }));
    }

    @Hook('87fc28fb-d21f-4e68-9dc9-f73b3bc1b7f0')
    public checkUserGroup(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.checkUserGroup(inputArtifact.object.systemId, inputArtifact.object.userGroup);
            }));
    }

    @Hook('ff3a8cd7-44d7-42a4-af89-75f4074902c4')
    public createMD5Hash(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.createMD5Hash(inputArtifact.object.userId);
            }));
    }


    @Hook('f70ee3e5-2064-4402-bb41-3542c9a194a3')
    public disableParentUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.disableParentUser(inputArtifact.object.systemId, inputArtifact.object.userId, inputArtifact.object.parentUserId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('4090e320-b0ba-4ed6-8d10-0717b76b2923')
    public getUsersByUserGroup(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getUsersByUserGroup(inputArtifact.object.userGroupId, inputArtifact.object.systemId, inputArtifact.object.page, inputArtifact.object.pageSize);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('196d7d1c-206b-480f-a5e1-102ed37f5a10')
    public getRolesBySystem(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getRolesBySystem(inputArtifact.object.systemId, inputArtifact.object.page, inputArtifact.object.pageSize);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('b72f3b83-ce14-41d1-a4c8-f719e8c1eeaf')
    public getRole(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getRole(inputArtifact.object.userId, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('4211ffad-af50-46d9-aa5c-4a8f1ff7897d')
    public currentUserBySystem(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getUserDetailByToken(this.context.token.split(' ')[1], inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('aafec36c-d0fd-4c9c-9212-2893ad1a6403')
    public lockUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.lockUser(inputArtifact.object.locked, inputArtifact.object.userId, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('ced78c48-82d3-4bec-b97f-72c0de5c9e4c')
    public unlockUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.unlockUser(inputArtifact.object.userId, inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }
    @Hook('2c4e91b3-8913-49a2-9a78-e2b7306430d6')
    public getUserSystem(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('AuthSecurityService')
            .pipe(concatMap((service: any) => {
                return service.getUserSystem(inputArtifact.object.systemId);
            })).pipe(map((any: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = any;
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('b89d7183-cbe5-404f-b0d0-5a500b18c394')
    public generateTokenToSystemUser(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.make<HttpHook>(HttpHook)
            .pipe(concatMap((httpHook: HttpHook) => {
                return this.settings
                    .pipe(concatMap((settings: any) => {

                        const endpoint = settings.environment == EnvironmentKind.DEV
                            ? 'https://api.local.nickbuilder.com.br/api/process/start/new'
                            : 'https://api.fluna.io/api/process/start/new';

                        const httpArtifact = new NickProcessArtfact();
                        httpArtifact.kind = ArtfactKind.ANY;
                        httpArtifact.object = {
                            config: {
                                url: `${endpoint}/b89d7183-cbe5-404f-b0d0-5a500b18c394`,
                                params: {},
                                headers: {
                                    'nick-system': '774bfaa5-fab2-4528-80f1-293afb8eb71d',
                                    'authorization': this.context.token
                                },
                                body: inputArtifact.object,
                                statusError: [404, 401]
                            }
                        };

                        return httpHook.postExternal(httpArtifact);
                    }));
            }))
            .pipe(map((response: NickProcessArtfact) => {
                return response;
            }));
    }
}
